<template>
  <div class="ad-hierarchy">
    <p class="ad-hierarchy__title mb-0">Task/Assignment Hierarchy</p>
    <v-divider></v-divider>

    <div class="ad-hierarchy__body">
      <!-- loading -->
      <div v-if="loading" class="ad-hierarchy__placeholder">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>

      <!-- failed to load -->
      <div v-else-if="error" class="ad-hierarchy__placeholder">
        <div>
          <p class="text-center mb-0">The hierarchy could not be loaded.</p>
          <p class="text-center mb-0 ad-hierarchy__placeholder--action" @click="loadHierarchy">
            Try again
          </p>
        </div>
      </div>

      <!-- assignment has no parent task -->
      <div v-else-if="!hasParentTask" class="ad-hierarchy__placeholder">
        <div>
          <p class="text-center mb-0">This assignment has no parent task yet.</p>
          <p class="text-center mb-0">Publish the assignment to see</p>
          <p class="text-center mb-0">its task hierarchy.</p>
        </div>
      </div>

      <!-- tree -->
      <div v-else class="ad-hierarchy__tree">
        <!-- parent task -->
        <div class="ad-hierarchy__node ad-hierarchy__node--task">
          <ad_form_icons :icon="'hierarchy_task'" class="ad-hierarchy__node--icon" />
          <p class="ad-hierarchy__node--label mb-0" :title="taskLabel">{{ taskLabel }}</p>
          <span class="ad-hierarchy__node--count">({{ assignmentsCountLabel }})</span>
        </div>

        <!-- child assignments -->
        <div class="ad-hierarchy__children">
          <div
            v-for="(assignment, index) in assignments"
            :key="assignment.id || `assignment-${index}`"
            class="ad-hierarchy__node ad-hierarchy__node--assignment"
            :class="{
              'ad-hierarchy__node--current': isCurrent(assignment),
              'ad-hierarchy__node--disabled': !assignment.id
            }"
            :title="assignment.id ? assignmentLabel(assignment) : 'This assignment cannot be opened — no ID was returned for it'"
            @click="selectAssignment(assignment)"
          >
            <ad_form_icons :icon="'hierarchy_assignment'" class="ad-hierarchy__node--icon" />
            <p class="ad-hierarchy__node--label ad-hierarchy__node--label--wrap mb-0">
              {{ assignmentLabel(assignment) }}
            </p>
            <div class="ad-hierarchy__node--actions" v-if="isCurrent(assignment)">
              <button
                type="button"
                class="ad-hierarchy__node--actions-button"
                :class="{ 'is-inert': !actionsEnabled }"
                :title="actionsEnabled ? 'Duplicate assignment' : 'Duplicating an assignment is available once assignment publishing is released'"
                @click.stop.prevent="duplicateAssignment(assignment)"
              >
                <pmdashboard_table_icons :icon="'pmdashboard_copy'" />
              </button>
              <button
                type="button"
                class="ad-hierarchy__node--actions-button"
                :class="{ 'is-inert': !actionsEnabled }"
                :title="actionsEnabled ? 'Delete assignment' : 'Deleting an assignment is available once assignment publishing is released'"
                @click.stop.prevent="deleteAssignment(assignment)"
              >
                <ad_form_icons :icon="'hierarchy_delete'" />
              </button>
            </div>
          </div>

          <!-- add assignment -->
          <div
            class="ad-hierarchy__node ad-hierarchy__node--add"
            :class="{ 'ad-hierarchy__node--inert': !actionsEnabled }"
            :title="actionsEnabled ? 'Add an assignment to this task' : 'Adding an assignment is available once assignment publishing is released'"
            @click="addAssignment"
          >
            <ad_form_icons :icon="'hierarchy_add'" class="ad-hierarchy__node--icon" />
            <p class="ad-hierarchy__node--label ad-hierarchy__node--label--add mb-0">add assignment</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// internal
import { newGetTaskById, getTasks, getAssignments } from "@/utils/newDbUtils.js";
import ad_form_icons from "@/assets/icons/ad_form_icons.vue";
import pmdashboard_table_icons from "@/assets/icons/pmdashboard_table_icons.vue";

export default {
  name: "ADFormHierarchy",

  components: {
    ad_form_icons,
    pmdashboard_table_icons
  },

  props: {
    // Record id of the parent task (`task.id` on the assignment payload).
    taskDbId: {
      type: [String, Number],
      default: null
    },
    // Readable Cue Task ID — the fallback used to resolve `taskDbId` when the
    // assignment payload only carries the readable value.
    taskId: {
      type: [String, Number],
      default: null
    },
    // Parent task name held in the form, shown until the task itself loads.
    taskName: {
      type: String,
      default: ""
    },
    // Assignment currently open in the form — highlighted in the tree.
    currentAssignmentId: {
      type: [String, Number],
      default: null
    },
    // Duplicate / delete / add stay inert until assignment publishing exists
    // (ADForm.publishAssignment is still a stub), so the panel shows the
    // affordances without pretending they work.
    actionsEnabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      error: false,
      task: null,
      assignments: []
    };
  },

  computed: {
    hasParentTask() {
      return !!(this.task || this.taskDbId || this.taskId);
    },
    taskLabel() {
      return (
        this.task?.taskName ||
        this.taskName ||
        this.task?.taskId ||
        this.taskId ||
        "Untitled task"
      );
    },
    assignmentsCountLabel() {
      const count = this.assignments.length;
      return `${count} ${count === 1 ? "assignment" : "assignments"}`;
    }
  },

  watch: {
    taskDbId() {
      this.loadHierarchy();
    },
    taskId() {
      this.loadHierarchy();
    }
  },

  methods: {
    // The /task payload nests nothing, the /assignment payload nests the same
    // values under `assignmentDetails` — read both so either source renders.
    normalizeAssignment(el) {
      const details = el?.assignmentDetails || el || {};
      return {
        id: el?.id ?? null,
        geo: details.geo || null,
        title: details.assignmentTitle || null,
        status: details.assignmentStatus || null
      };
    },
    assignmentLabel(assignment) {
      if (assignment.geo && assignment.title)
        return `${assignment.geo} | ${assignment.title}`;
      return assignment.geo || assignment.title || "Untitled assignment";
    },
    isCurrent(assignment) {
      return (
        !!assignment.id &&
        !!this.currentAssignmentId &&
        String(assignment.id) === String(this.currentAssignmentId)
      );
    },
    // The /task endpoints work off the record id. When the assignment payload
    // only carried the readable Cue Task ID, look the task up by it first.
    async resolveTaskDbId() {
      if (this.taskDbId) return this.taskDbId;
      if (!this.taskId) return null;
      const resp = await getTasks({ query: { search: this.taskId } });
      const match = (resp?.content || []).find(
        (el) => el.cueTaskId === this.taskId || el.taskId === this.taskId
      );
      return match?.id ?? null;
    },
    // Fallback source: the assignment list always carries record ids, which the
    // task payload's `assignments` may not. Without an id a node can't be opened.
    async loadAssignmentsByCueTaskId(cueTaskId) {
      if (!cueTaskId) return [];
      const resp = await getAssignments({ query: { search: cueTaskId } });
      return (resp?.content || [])
        .filter((el) => el.cueTaskId === cueTaskId)
        .map((el) => this.normalizeAssignment(el));
    },
    async loadHierarchy() {
      if (!this.hasParentTask) {
        this.task = null;
        this.assignments = [];
        return;
      }
      this.loading = true;
      this.error = false;
      try {
        const taskDbId = await this.resolveTaskDbId();
        // The assignment points at a parent task, so failing to resolve it is a
        // fault worth surfacing rather than an empty tree.
        if (!taskDbId) {
          this.task = null;
          this.assignments = [];
          this.error = true;
          return;
        }
        const resp = await newGetTaskById(taskDbId);
        if (resp?.status !== 200 || !resp.data) {
          this.error = true;
          return;
        }
        this.task = resp.data;
        let assignments = (resp.data.assignments || []).map((el) =>
          this.normalizeAssignment(el)
        );
        // Every node has to be clickable — if the task payload left the ids out,
        // rebuild the list from the assignment endpoint instead.
        if (!assignments.length || assignments.some((el) => !el.id)) {
          const fallback = await this.loadAssignmentsByCueTaskId(
            resp.data.taskId || this.taskId
          );
          if (fallback.length) assignments = fallback;
        }
        this.assignments = assignments;
      } catch (err) {
        console.error("Failed to load the task hierarchy:", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    selectAssignment(assignment) {
      if (!assignment.id || this.isCurrent(assignment)) return;
      this.$emit("select-assignment", assignment.id);
    },
    duplicateAssignment(assignment) {
      if (!this.actionsEnabled) return;
      this.$emit("duplicate-assignment", assignment);
    },
    deleteAssignment(assignment) {
      if (!this.actionsEnabled) return;
      this.$emit("delete-assignment", assignment);
    },
    addAssignment() {
      if (!this.actionsEnabled) return;
      this.$emit("add-assignment");
    }
  },

  mounted() {
    this.loadHierarchy();
  }
};
</script>

<style lang="scss" scoped>
.ad-hierarchy {
  font-family: 'Roboto', sans-serif !important;
  background-color: #fff;
  // Matches the details column's height so the two end level and the modal
  // itself never needs a scrollbar — the tree scrolls inside its own body.
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__title {
    font-size: 20px;
    font-weight: 400;
    line-height: 26px;
    color: #656F7D;
    padding: 16px 20px;
  }

  &__body {
    // The panel's only scroll container.
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 12px 20px 12px;

    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background: #ADB3BD;
      border-radius: 8px;
    }
    & {
      scrollbar-width: thin;
      scrollbar-color: #ADB3BD #f1f1f1;
    }
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    & p {
      font-family: Roboto;
      font-size: 16px;
      font-weight: 400;
      line-height: 18px;
      text-align: center;
      color: #B4B4B4;
    }
    &--action {
      color: #1976D2 !important;
      margin-top: 8px !important;
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  &__children {
    // The guide line that ties the child assignments to the parent task.
    position: relative;
    margin-left: 14px;
    padding-left: 14px;
    border-left: 1px solid #E8EAED;
  }

  &__node {
    display: flex;
    align-items: flex-start;
    padding: 7px 8px;
    border-radius: 4px;
    position: relative;

    &--icon {
      flex-shrink: 0;
      margin-right: 8px;
      margin-top: 2px;
    }

    &--label {
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
      color: #656F7D;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      &--wrap {
        white-space: normal;
        word-wrap: break-word;
      }
      &--add {
        color: #697A9B;
      }
    }

    &--count {
      flex-shrink: 0;
      margin-left: 6px;
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      color: #93979D;
      white-space: nowrap;
    }

    &--task {
      font-weight: 500;
      & .ad-hierarchy__node--label {
        font-weight: 500;
        color: #656F7D;
      }
    }

    &--assignment {
      cursor: pointer;
      padding-right: 52px;
      &:hover {
        background-color: #F2F2F2;
      }
    }

    &--current {
      background-color: #F2F2F2;
      & .ad-hierarchy__node--label {
        color: #000;
      }
    }

    &--add {
      cursor: pointer;
      margin-top: 2px;
      &:hover {
        background-color: #F2F2F2;
      }
    }

    // No record id came back for the node, so it cannot be opened.
    &--disabled {
      cursor: not-allowed;
      & .ad-hierarchy__node--label {
        color: #B4B4B4;
      }
    }

    // Rendered exactly as designed but not yet wired — Phase 2 turns these on
    // via `actionsEnabled`; the tooltip explains until then.
    &--inert {
      cursor: not-allowed;
      &:hover {
        background-color: transparent;
      }
    }

    &--actions {
      position: absolute;
      right: 6px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;

      &-button {
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        padding: 0;
        border-radius: 2px;
        margin-left: 2px;
        &:hover {
          background-color: #DEDEDE;
        }
        &.is-inert {
          cursor: not-allowed;
          &:hover {
            background-color: transparent;
          }
        }
      }
    }
  }
}
</style>
